import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  ArrowRight, Award, Briefcase, Music, Disc3, Sparkles, Users, GraduationCap, Eye,
  MessageSquare, Palette, Heart, Tag, Route as RouteIcon, Droplets, ShieldCheck,
} from "lucide-react";
import { SiteNav } from "@/components/SiteNav";
import { createCheckout } from "@/lib/api";

const FRANK_PHOTO =
  "https://customer-assets.emergentagent.com/job_leak-finder-web/artifacts/3l286q7f_Screenshot_20260705_225108_Gallery.jpg";

const fadeUp = { hidden: { opacity: 0, y: 24 }, show: { opacity: 1, y: 0 } };

const CREDIBILITY = [
  { icon: Award, title: "20+ Years of Experience", text: "Promotions, hospitality, live events, marketing, and customer-facing business operations." },
  { icon: Briefcase, title: "Owner / Operator Since 2005", text: "Founder and operator of Tycoon Promotions." },
  { icon: Music, title: "Music Marketing Experience", text: "Experience supporting artist development, promotions, campaigns, live activations, and entertainment marketing." },
  { icon: Disc3, title: "2× Platinum Plaque Recipient", text: "Recognition connected to work surrounding major music projects." },
  { icon: Sparkles, title: "Premium Hospitality & Live Events", text: "More than two decades of experience in high-volume, customer-facing event environments." },
  { icon: Users, title: "Multi-Sided Business Perspective", text: "Experience as a customer, employee, manager, promoter, operator, and owner." },
  { icon: GraduationCap, title: "Howard University", text: "Business Marketing — education and exposure." },
  { icon: Eye, title: "Customer Experience Lens", text: "Experience identifying where communication, presentation, service, and expectations disconnect." },
];

const LOOKS_FOR = [
  { icon: MessageSquare, title: "Messaging", text: "Is it immediately clear what the business does, who it serves, and why it matters?" },
  { icon: Palette, title: "Visual Presentation", text: "Does the business look trustworthy, intentional, and consistent?" },
  { icon: Heart, title: "Customer Experience", text: "Where could customers feel confused, disconnected, overlooked, or frustrated?" },
  { icon: Tag, title: "Offer Clarity", text: "Can customers quickly understand what they are buying?" },
  { icon: RouteIcon, title: "Path to Action", text: "Is there a clear next step from attention to inquiry, booking, purchase, or follow-up?" },
  { icon: Droplets, title: "Conversion Leaks", text: "Where might interest be disappearing before it becomes revenue?" },
];

export default function AboutFrank() {
  const navigate = useNavigate();
  const [payOpen, setPayOpen] = useState(false);
  const [product, setProduct] = useState({ code: "BASIC", price: 49, name: "Leak Finder Survey + Basic Results" });
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const FALLBACK = {
    BASIC: { price: 49, name: "Leak Finder Survey + Basic Results" },
    STRATEGY: { price: 97, name: "30-Minute Strategy Call" },
    BUNDLE: { price: 129, name: "Audit + Strategy Call Bundle" },
  };
  const openPay = (productCode) => {
    const p = FALLBACK[productCode];
    setProduct({ code: productCode, price: p.price, name: p.name });
    setPayOpen(true);
  };

  const startPayment = async () => {
    if (!name.trim() || !email.trim()) {
      toast.error("Please enter your name and email.");
      return;
    }
    setLoading(true);
    try {
      const { url } = await createCheckout({
        buyer_email: email.trim(),
        buyer_name: name.trim(),
        product_code: product.code,
        origin_url: window.location.origin,
      });
      window.location.href = url;
    } catch (e) {
      toast.error("Could not start checkout. Please try again.");
      setLoading(false);
    }
  };

  const label = "text-xs sm:text-sm font-heading font-semibold tracking-[0.2em] text-brand-gold uppercase";

  return (
    <div className="min-h-screen bg-brand-bone text-brand-ink">
      <SiteNav onStartAudit={() => openPay("BASIC")} />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-brand-green/[0.05] to-transparent pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-16 md:pt-24 md:pb-24 relative">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <motion.div initial="hidden" animate="show" variants={fadeUp} transition={{ duration: 0.6 }} className="order-2 lg:order-1">
              <p className={label + " mb-5"}>About Frank</p>
              <h1 className="font-heading font-extrabold tracking-tight leading-[1.05] text-4xl sm:text-5xl lg:text-6xl text-brand-ink">
                Meet Frank Marina III.
              </h1>
              <p className="mt-5 text-lg md:text-xl text-brand-green font-medium leading-relaxed">
                20+ years of seeing business from every side of the customer experience.
              </p>
              <p className="mt-6 text-brand-ink/75 leading-relaxed">
                Frank Marina III is a Visual Brand Strategist and Experience Architect with more than
                20 years of experience across promotions, hospitality, music marketing, live event
                environments, and customer-facing business operations.
              </p>
              <p className="mt-4 text-brand-ink/75 leading-relaxed">
                He helps business owners see what their customers see, identify what feels unclear or
                disconnected, and create a cleaner path from attention to trust, action, and revenue.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <Button
                  data-testid="about-hero-audit-btn"
                  onClick={() => openPay("BASIC")}
                  className="rounded-full bg-brand-gold hover:bg-brand-goldDark text-white text-sm font-semibold uppercase tracking-wide px-8 py-6 shadow-lg shadow-brand-gold/25 hover:-translate-y-0.5 transition-all"
                >
                  Take the $49 Leak Finder Audit <ArrowRight className="w-5 h-5 ml-1" />
                </Button>
                <Button
                  data-testid="about-hero-strategy-btn"
                  variant="outline"
                  onClick={() => openPay("STRATEGY")}
                  className="rounded-full border-brand-green text-brand-green hover:bg-brand-green/5 text-sm font-semibold uppercase tracking-wide px-8 py-6"
                >
                  Book a Strategy Call
                </Button>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7 }}
              className="order-1 lg:order-2"
            >
              <div className="relative mx-auto max-w-sm lg:max-w-none">
                <div className="absolute -inset-3 rounded-[2rem] bg-brand-green/10 -z-10" />
                <img
                  src={FRANK_PHOTO}
                  alt="Frank Marina III"
                  className="w-full rounded-[1.75rem] object-cover shadow-2xl ring-1 ring-brand-green/10 aspect-[4/5]"
                  style={{ objectPosition: "50% 20%" }}
                  data-testid="about-frank-photo"
                />
                <div className="absolute bottom-4 left-4 right-4 rounded-2xl bg-brand-green/90 backdrop-blur text-white px-5 py-3 shadow-lg">
                  <p className="font-heading font-bold text-lg leading-tight">Frank Marina III</p>
                  <p className="text-xs text-brand-gold uppercase tracking-wide font-semibold">Tycoon Promotions · 3E Audit</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* WHY THE 3E LEAK FINDER EXISTS */}
      <section className="bg-brand-green text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
          <p className="text-xs sm:text-sm font-heading font-semibold tracking-[0.2em] text-brand-gold uppercase">Why It Exists</p>
          <h2 className="font-heading text-3xl sm:text-4xl font-extrabold mt-4 leading-tight">
            The 3E Leak Finder Wasn't Built From Theory Alone.
          </h2>
          <p className="mt-6 text-white/80 text-lg leading-relaxed">
            Frank has spent decades working inside businesses, live events, hospitality environments,
            marketing campaigns, customer interactions, and high-pressure customer-facing experiences.
          </p>
          <p className="mt-4 text-white/80 text-lg leading-relaxed">
            That experience has allowed him to see firsthand where expectations break down, where
            communication gets lost, where presentation weakens trust, and where businesses quietly
            lose money, attention, and opportunity.
          </p>
          <p className="mt-4 text-white/80 text-lg leading-relaxed">
            The 3E Leak Finder was created to help business owners step outside of their own
            perspective, see what customers may be experiencing, and identify the leaks standing
            between attention and revenue.
          </p>
        </div>
      </section>

      {/* SIGNATURE POSITIONING */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
        <p className={label}>His Lens</p>
        <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold mt-4 text-brand-green">
          Two Decades. Multiple Lanes. One Sharp Lens.
        </h2>
        <p className="mt-6 text-brand-ink/75 text-lg leading-relaxed">
          Frank's perspective was built across different industries, roles, and environments. He has
          seen business from the viewpoint of the customer, employee, manager, promoter, operator, and owner.
        </p>
        <p className="mt-4 text-brand-ink/75 text-lg leading-relaxed">
          That range gives him a different lens: the ability to connect brand presentation, customer
          experience, messaging, operations, and conversion instead of looking at each area in isolation.
        </p>
      </section>

      {/* CREDIBILITY GRID */}
      <section className="bg-white border-y border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
          <p className={label}>The Background</p>
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold mt-4">
            Why Frank is qualified to spot what you can't.
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-12">
            {CREDIBILITY.map((c, i) => (
              <motion.div
                key={c.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: (i % 4) * 0.08 }}
                className="rounded-xl border border-border bg-brand-bone p-6 hover:shadow-md transition-shadow"
                data-testid={`credibility-card-${i + 1}`}
              >
                <div className="w-11 h-11 rounded-xl bg-brand-green/10 flex items-center justify-center text-brand-green mb-4">
                  <c.icon className="w-5 h-5" />
                </div>
                <h3 className="font-heading text-base font-bold leading-snug">{c.title}</h3>
                <p className="mt-2 text-sm text-brand-ink/70 leading-relaxed">{c.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT FRANK ACTUALLY LOOKS FOR */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
        <div className="max-w-2xl">
          <p className={label}>The Approach</p>
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold mt-4 text-brand-green">
            Frank Doesn't Just Look at Your Logo.
          </h2>
          <p className="mt-5 text-brand-ink/75 text-lg leading-relaxed">
            The 3E approach looks at the full experience around the business — what customers see,
            understand, feel, and are asked to do next.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
          {LOOKS_FOR.map((c, i) => (
            <motion.div
              key={c.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: (i % 3) * 0.1 }}
              className="rounded-xl border border-border bg-white p-7 hover:shadow-md transition-shadow"
              data-testid={`looksfor-card-${i + 1}`}
            >
              <div className="w-11 h-11 rounded-xl bg-brand-gold/15 flex items-center justify-center text-brand-goldDark mb-4">
                <c.icon className="w-5 h-5" />
              </div>
              <h3 className="font-heading text-lg font-semibold">{c.title}</h3>
              <p className="mt-2 text-brand-ink/70 leading-relaxed">{c.text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* WHY TRUST THIS AUDIT */}
      <section className="bg-brand-green text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
          <div className="w-12 h-12 rounded-full bg-brand-gold/20 flex items-center justify-center text-brand-gold mb-6">
            <ShieldCheck className="w-6 h-6" />
          </div>
          <p className="text-xs sm:text-sm font-heading font-semibold tracking-[0.2em] text-brand-gold uppercase">Why Trust This Audit?</p>
          <h2 className="font-heading text-3xl sm:text-4xl font-extrabold mt-4 leading-tight">
            This Is More Than a Survey.
          </h2>
          <p className="mt-6 text-white/80 text-lg leading-relaxed">
            The survey collects the information. The scoring helps organize the information. Technology
            can help identify patterns.
          </p>
          <p className="mt-4 text-white/80 text-lg leading-relaxed">
            But the value of the 3E Leak Finder comes from combining those insights with real-world
            experience, customer perspective, business judgment, and the ability to see connections
            across the full customer journey.
          </p>
          <p className="mt-6 text-brand-gold font-heading text-xl font-bold">
            There is an experienced person behind the lens.
          </p>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28 text-center">
        <Droplets className="w-10 h-10 text-brand-gold mx-auto" />
        <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold mt-6">
          See Your Business the Way Your Customer Does.
        </h2>
        <p className="mt-5 text-brand-ink/75 text-lg max-w-2xl mx-auto leading-relaxed">
          You may already know your business from the inside. The 3E Leak Finder helps you step outside of it.
        </p>
        <div className="mt-6 flex flex-col items-center gap-1 text-brand-ink/80 font-medium">
          <span>Identify what is clear.</span>
          <span>Find what is disconnected.</span>
          <span>See what may be costing you attention, trust, and revenue.</span>
        </div>

        <div className="mt-8 inline-flex flex-col items-center rounded-2xl border border-border bg-white px-8 py-5 shadow-sm">
          <p className="text-sm text-brand-ink/70">30-Minute Results Review & Strategy Call</p>
          <div className="mt-1 flex items-baseline gap-3">
            <span className="text-xl text-brand-ink/40 line-through" data-testid="about-strategy-old-price">$149</span>
            <span className="font-heading text-4xl font-extrabold text-brand-green" data-testid="about-strategy-price">$97</span>
            <span className="text-brand-ink/60 text-sm">now</span>
          </div>
        </div>

        <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
          <Button
            data-testid="about-final-audit-btn"
            onClick={() => openPay("BASIC")}
            className="rounded-full bg-brand-gold hover:bg-brand-goldDark text-white text-sm font-semibold uppercase tracking-wide px-10 py-6 shadow-lg hover:-translate-y-0.5 transition-all"
          >
            Start the $49 Leak Finder Audit
          </Button>
          <Button
            data-testid="about-final-bundle-btn"
            variant="outline"
            onClick={() => openPay("BUNDLE")}
            className="rounded-full border-brand-green text-brand-green hover:bg-brand-green/5 text-sm font-semibold uppercase tracking-wide px-10 py-6"
          >
            Audit + Strategy Call
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border bg-brand-bone">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 text-center">
          <p className="text-brand-ink/70">
            Powered by <strong className="text-brand-green">Tycoon Promotions</strong> and{" "}
            <strong className="text-brand-green">3E Audit</strong>
          </p>
          <nav className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm">
            <button onClick={() => navigate("/")} className="text-brand-ink/60 hover:text-brand-green transition-colors" data-testid="about-footer-home">Home</button>
            <a href="mailto:frankmarina@3eleakfinder.com" className="text-brand-ink/60 hover:text-brand-green transition-colors">Contact</a>
            <a href="/privacy" className="text-brand-ink/60 hover:text-brand-green transition-colors">Privacy Policy</a>
            <a href="/terms" className="text-brand-ink/60 hover:text-brand-green transition-colors">Terms</a>
            <a href="/refund" className="text-brand-ink/60 hover:text-brand-green transition-colors">Refund Policy</a>
          </nav>
        </div>
      </footer>

      {/* Payment dialog */}
      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent data-testid="about-payment-dialog" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">{product.name}</DialogTitle>
            <DialogDescription>
              ${product.price} one-time · Enter your details to continue to secure Stripe checkout.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="about-pay-name">Your name</Label>
              <Input id="about-pay-name" data-testid="about-payment-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Jane Owner" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="about-pay-email">Email address</Label>
              <Input id="about-pay-email" data-testid="about-payment-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@business.com" />
            </div>
          </div>
          <DialogFooter>
            <Button
              data-testid="about-payment-submit-button"
              onClick={startPayment}
              disabled={loading}
              className="w-full rounded-full bg-brand-gold hover:bg-brand-goldDark text-white"
            >
              {loading ? "Redirecting…" : `Continue to Checkout · $${product.price}`}
            </Button>
          </DialogFooter>
          <p className="text-xs text-brand-ink/50 text-center leading-relaxed">
            By purchasing, you agree to our{" "}
            <a href="/terms" target="_blank" rel="noopener noreferrer" className="underline hover:text-brand-green">Terms</a> and{" "}
            <a href="/refund" target="_blank" rel="noopener noreferrer" className="underline hover:text-brand-green">Refund Policy</a>.
          </p>
        </DialogContent>
      </Dialog>
    </div>
  );
}
